import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, ArrowLeft, Mail } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";

export default function ForgotPasswordPage() {
    const { lang } = useI18n();
    const [email, setEmail]       = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent]         = useState(false);
    const [error, setError]       = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSent(true);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                (lang === "ar" ? "حدث خطأ. يرجى المحاولة لاحقًا." : "Something went wrong. Please try again.")
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <div className="w-full max-w-md">

                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <img src="/hemaia_logo.svg" alt="Hemaia" className="h-10" />
                </div>

                <div className="surface-card p-8">
                    {sent ? (
                        <div className="text-center py-4">
                            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                            <h2 className="text-xl font-semibold text-foreground mb-2">
                                {lang === "ar" ? "تحقق من بريدك الإلكتروني" : "Check your email"}
                            </h2>
                            <p className="text-sm text-muted-foreground mb-6">
                                {lang === "ar"
                                    ? "إذا كان هذا البريد الإلكتروني مسجلاً لدينا، ستصلك رسالة تحتوي على رابط إعادة تعيين كلمة المرور. تحقق من مجلد الرسائل غير المرغوب فيها إذا لم تجدها."
                                    : "If an account exists for this email, you'll receive a reset link shortly. Check your spam folder if you don't see it within a few minutes."}
                            </p>
                            <Link
                                to="/login"
                                className="text-sm font-medium text-primary hover:underline"
                            >
                                {lang === "ar" ? "← العودة لتسجيل الدخول" : "← Back to sign in"}
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <Mail className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-semibold text-foreground">
                                        {lang === "ar" ? "نسيت كلمة المرور؟" : "Forgot your password?"}
                                    </h1>
                                    <p className="text-sm text-muted-foreground">
                                        {lang === "ar"
                                            ? "أدخل بريدك لإرسال رابط إعادة التعيين"
                                            : "Enter your email and we'll send a reset link"}
                                    </p>
                                </div>
                            </div>

                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        {lang === "ar" ? "البريد الإلكتروني" : "Email address"}
                                    </label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        autoFocus
                                        placeholder={lang === "ar" ? "example@email.com" : "you@example.com"}
                                        className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                    />
                                </div>

                                {error && (
                                    <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={submitting || !email}
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-95 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                    {lang === "ar" ? "إرسال رابط الاسترداد" : "Send Reset Link"}
                                </button>
                            </form>

                            <div className="mt-5 text-center">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <ArrowLeft className="h-3.5 w-3.5" />
                                    {lang === "ar" ? "العودة لتسجيل الدخول" : "Back to sign in"}
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
